/* eslint-env browser */
// Reports › Contact sync report (wave 3, W2_Portal_Tables look; admin only):
// figure tiles for how many people synced their phone contacts today, in the
// last 7 and 30 days, this year and in all, then the people in the modern
// table, most recent sync first.
//
// Data, as before: the users list already in the store (usersdata.users),
// nothing is read or written here. Days start at midnight Addis Ababa time
// and every time is shown on the 12-hour clock (owner rule). Phone and email
// are hidden in demo mode; the CSV needs critical edits on, as before.
// Call-centre feedback (29 Oct 2026): a copy button beside each number copies
// it without spaces (+251964540448) for MicroSIP; it is not there while
// numbers are hidden.
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import TodayOutlinedIcon from '@mui/icons-material/TodayOutlined';
import DateRangeOutlinedIcon from '@mui/icons-material/DateRangeOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import ContactsOutlinedIcon from '@mui/icons-material/ContactsOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import { ADDIS_OFFSET_MS } from '../common/exportUtils';
import DashboardCard from '../components/DashboardCard';
import { CopyButton, DataTable, StatusChip, calmCaps, formatNumber, formatPhoneDisplay, phoneMatches, toMs } from '../components/modern';

const DAY_MS = 24 * 60 * 60 * 1000;
// Midnight (Addis Ababa) of the day `ms` falls in.
const addisDayStart = (ms) => Math.floor((ms + ADDIS_OFFSET_MS) / DAY_MS) * DAY_MS - ADDIS_OFFSET_MS;
const addisYearStart = (ms) => Date.UTC(new Date(ms + ADDIS_OFFSET_MS).getUTCFullYear(), 0, 1) - ADDIS_OFFSET_MS;

const ContactSyncReport = () => {
  const { t, i18n } = useTranslation();
  const settings = useSelector((state) => state.settingsdata.settings);
  const usersdata = useSelector((state) => state.usersdata);
  const allowPrivate = !!settings.AllowCriticalEditsAdmin;
  const hidden = t('hidden_demo');
  const phoneWord = t('phone', { defaultValue: 'Phone number' });

  // People who synced contacts, and the figures, from the loaded list.
  const { data, statistics, indexOf } = useMemo(() => {
    const stats = {
      today: { count: 0, contacts: 0 },
      lastWeek: { count: 0, contacts: 0 },
      lastMonth: { count: 0, contacts: 0 },
      allYear: { count: 0, contacts: 0 },
      total: { count: 0, contacts: 0 },
    };
    if (!usersdata.users) return { data: [], statistics: stats, indexOf: new Map() };
    // Filter users who have synced contacts
    const syncedUsers = usersdata.users.filter((user) => user.contactSynced === true && user.contactCount > 0);

    const now = Date.now();
    const todayStart = addisDayStart(now);
    const weekAgo = addisDayStart(now - 7 * DAY_MS);
    const monthAgo = addisDayStart(now - 30 * DAY_MS);
    const yearStart = addisYearStart(now);

    syncedUsers.forEach((user) => {
      const syncDate = toMs(user.contactSyncedAt);
      const contactCount = user.contactCount || 0;

      // Total
      stats.total.count++;
      stats.total.contacts += contactCount;

      if (syncDate !== null) {
        if (syncDate > todayStart) {
          stats.today.count++;
          stats.today.contacts += contactCount;
        }
        if (syncDate > weekAgo) {
          stats.lastWeek.count++;
          stats.lastWeek.contacts += contactCount;
        }
        if (syncDate > monthAgo) {
          stats.lastMonth.count++;
          stats.lastMonth.contacts += contactCount;
        }
        if (syncDate > yearStart) {
          stats.allYear.count++;
          stats.allYear.contacts += contactCount;
        }
      }
    });
    const index = new Map();
    syncedUsers.forEach((user, i) => index.set(user, i));
    return { data: syncedUsers, statistics: stats, indexOf: index };
  }, [usersdata.users]);

  const sno = (row) => (indexOf.has(row) ? indexOf.get(row) + 1 : '');
  const usersWord = t('users', { defaultValue: 'Users' });
  const contactsWord = calmCaps(t('contacts', { defaultValue: 'Contacts' }));
  const tile = (key, title, icon, hue, s) => (
    <DashboardCard
      key={key}
      icon={icon}
      hue={hue}
      title={title}
      stats={[
        { label: usersWord, value: formatNumber(s.count) || '0' },
        { label: contactsWord, value: formatNumber(s.contacts) || '0' },
      ]}
    />
  );

  const columns = [
    { id: 'sno', title: t('s_no'), sortValue: (row) => sno(row), searchable: false, width: 64, render: (row) => sno(row) },
    { id: 'firstName', title: t('firstname'), field: 'firstName', render: (row) => row.firstName || '-' },
    { id: 'lastName', title: t('lastname'), field: 'lastName', render: (row) => row.lastName || '-' },
    {
      id: 'mobile',
      title: t('mobile'),
      field: 'mobile',
      nowrap: true,
      searchValue: () => '',
      sortValue: (row) => (allowPrivate ? row.mobile || '' : ''),
      render: (row) =>
        !allowPrivate ? (
          hidden
        ) : row.mobile ? (
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
            {formatPhoneDisplay(row.mobile) || row.mobile}
            <CopyButton value={row.mobile} phone what={phoneWord} sx={{ my: -0.5 }} />
          </Box>
        ) : (
          '-'
        ),
    },
    {
      id: 'email',
      title: t('email'),
      field: 'email',
      searchValue: (row) => (allowPrivate ? row.email || '' : ''),
      sortValue: (row) => (allowPrivate ? row.email || '' : ''),
      render: (row) => (allowPrivate ? row.email || '-' : hidden),
    },
    {
      id: 'usertype',
      title: t('usertype'),
      field: 'usertype',
      searchValue: (row) => (row.usertype ? t(row.usertype) : ''),
      render: (row) =>
        row.usertype ? (
          <StatusChip
            tone={row.usertype === 'driver' ? 'info' : 'accent'}
            icon={row.usertype === 'driver' ? <DirectionsCarOutlinedIcon /> : <PersonOutlineRoundedIcon />}
            label={t(row.usertype)}
          />
        ) : (
          '-'
        ),
    },
    {
      id: 'contactCount',
      title: t('contact_count'),
      field: 'contactCount',
      type: 'number',
      render: (row) => (
        <Box component="span" sx={{ fontWeight: 700 }}>
          {formatNumber(row.contactCount || 0)}
        </Box>
      ),
    },
    { id: 'contactSyncedAt', title: t('sync_date'), field: 'contactSyncedAt', type: 'datetime', defaultSort: 'desc' },
    {
      id: 'contactSynced',
      title: t('status'),
      field: 'contactSynced',
      searchValue: (row) => (row.contactSynced ? t('synced') : t('not_synced')),
      render: (row) => (
        <StatusChip
          tone={row.contactSynced ? 'success' : 'warning'}
          icon={row.contactSynced ? <CheckRoundedIcon /> : <CloseRoundedIcon />}
          label={row.contactSynced ? t('synced') : t('not_synced')}
        />
      ),
    },
  ];

  // The same CSV columns as before (the number is filled in now; the sync
  // time is Addis Ababa 12-hour instead of the browser's own format).
  const exportColumns = [
    { title: t('s_no'), value: (row) => sno(row) },
    { title: t('firstname'), field: 'firstName' },
    { title: t('lastname'), field: 'lastName' },
    { title: t('mobile'), value: (row) => (allowPrivate ? row.mobile || '' : hidden) },
    { title: t('email'), value: (row) => (allowPrivate ? row.email || '' : hidden) },
    { title: t('usertype'), field: 'usertype' },
    { title: t('contact_count'), field: 'contactCount' },
    { title: t('sync_date'), field: 'contactSyncedAt', type: 'datetime' },
    { title: t('status'), field: 'contactSynced' },
  ];

  // Name always; phone (by its digits) and email only when they may be shown.
  const searchFn = (row, q, ql) => {
    const name = `${row.firstName || ''} ${row.lastName || ''}`.toLowerCase();
    if (name.includes(ql)) return true;
    if (row.usertype && String(t(row.usertype)).toLowerCase().includes(ql)) return true;
    if (!allowPrivate) return false;
    if (phoneMatches(row.mobile, q)) return true;
    return String(row.email || '').toLowerCase().includes(ql);
  };

  const title = calmCaps(t('contact_sync_report', { defaultValue: 'Contact sync report' }));
  const loading = !usersdata.users && !!usersdata.loading;

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(5, minmax(0, 1fr))' } }}>
        {tile('today', t('today'), <TodayOutlinedIcon />, 'info', statistics.today)}
        {tile('week', t('last_week'), <DateRangeOutlinedIcon />, 'success', statistics.lastWeek)}
        {tile('month', t('last_month'), <CalendarMonthOutlinedIcon />, 'amber', statistics.lastMonth)}
        {tile('year', t('this_year'), <EventOutlinedIcon />, 'accent', statistics.allYear)}
        {tile('all', t('all_time'), <ContactsOutlinedIcon />, 'neutral', statistics.total)}
      </Box>
      <DataTable
        title={title}
        rows={data}
        columns={columns}
        loading={loading}
        searchFn={searchFn}
        resortKey={allowPrivate ? 'private' : 'demo'}
        searchPlaceholder={allowPrivate ? t('drivers_search_hint', { defaultValue: 'Search name, phone or email' }) : t('customers_search_name', { defaultValue: 'Search by name' })}
        prefsKey="contact_sync_report"
        exportCsv={allowPrivate}
        exportColumns={exportColumns}
        exportFileName="contact_sync_report"
        exportTitle={title}
        minWidth={1040}
        emptyState={({ query }) =>
          query ? null : { icon: <ContactsOutlinedIcon />, title: t('contact_sync_empty', { defaultValue: 'Nobody has synced contacts yet' }) }
        }
      />
    </Box>
  );
};

export default ContactSyncReport;
